import { useState } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import type { Currency } from 'dinero.js'
import { ResultConceptItemTableRowFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import MissingEmployeesModal from 'modules/payroll_bundle/components/MissingEmployeesModal'
import { usePayrollBundlePageContext } from 'modules/payroll_bundle/components/PayrollBundle'
import SummaryBox from 'modules/payroll_bundle/components/SummaryBox'
import SummaryTotal from 'modules/payroll_bundle/components/SummaryTotal'
import SummaryProgressBarBox from 'modules/payroll_bundle/components/results/SummaryProgressBarBox'
import type { PayrollResultConceptTotal } from 'modules/payroll_bundle/hooks/useResultConceptTotals'
import { DEFAULT_RESULT_CONCEPT } from 'modules/payroll_bundle/lib/constants'

import ResultStatsSidepanel from './ResultStatsSidepanel'

interface Props {
  currentPeriodResultConceptTotalsByConceptName: {
    [conceptName: string]: PayrollResultConceptTotal
  }
  currentPeriodResultConceptTotals: PayrollResultConceptTotal[]
  otherPeriodResultConceptTotalsByConceptName: { [conceptName: string]: PayrollResultConceptTotal }
  importedEmployeeIds: number[]
  currency: Currency
  mappedResultConcepts: ResultConceptItemTableRowFragment['concept'][]
  onOpenImportResultsModal: () => void
}

const ResultStats = ({
  currentPeriodResultConceptTotalsByConceptName,
  currentPeriodResultConceptTotals,
  otherPeriodResultConceptTotalsByConceptName,
  importedEmployeeIds,
  currency,
  mappedResultConcepts,
  onOpenImportResultsModal,
}: Props) => {
  const { filteredEmployees, policyPeriod, selectedPolicy } = usePayrollBundlePageContext()
  const [showResultStatsSidepanel, setShowResultStatsSidepanel] = useState(false)
  const [showMissingEmployeesModal, setShowMissingEmployeesModal] = useState(false)
  const totalNumberOfEmployees = filteredEmployees.length
  const totalNumberOfImportedEmployees = new Set(importedEmployeeIds).size

  function handleOpenSidepanel() {
    setShowResultStatsSidepanel(true)
  }

  function handleCloseSidepanel() {
    setShowResultStatsSidepanel(false)
  }

  function handleCloseMissingEmployeesModal() {
    setShowMissingEmployeesModal(false)
  }

  function handleOpenMissingEmployeesModal() {
    setShowMissingEmployeesModal(true)
  }

  function handleOpenImportResultsModal() {
    setShowMissingEmployeesModal(false)
    onOpenImportResultsModal()
  }

  return (
    <>
      <Box
        flexDirection="row"
        borderRadius={{ all: 'abs008' }}
        border={{ all: { color: 'grey400', style: 'solid', width: 's1' } }}
        paddingX="s24"
        gap="s24"
      >
        <SummaryProgressBarBox
          title={i18n.t('payroll_bundle.results_payments.results.summary.number_of_employees')}
          tooltipInformation={i18n.t(
            'payroll_bundle.results_payments.results.summary.number_of_results_less_than_employees',
            { number: totalNumberOfEmployees - totalNumberOfImportedEmployees }
          )}
          currentAmount={totalNumberOfImportedEmployees}
          totalAmount={totalNumberOfEmployees}
          onClick={handleOpenMissingEmployeesModal}
        />

        <SummaryTotal
          title={i18n.t('payroll_bundle.results_payments.results.summary.total_gross_salary')}
          totalCurrentPeriodInCents={
            currentPeriodResultConceptTotalsByConceptName[DEFAULT_RESULT_CONCEPT.gross_pay]?.total
          }
          totalPreviousPeriodInCents={
            otherPeriodResultConceptTotalsByConceptName[DEFAULT_RESULT_CONCEPT.gross_pay]?.total
          }
          currency={currency}
        />

        <SummaryTotal
          title={i18n.t('payroll_bundle.results_payments.results.summary.total_net_salary')}
          totalCurrentPeriodInCents={
            currentPeriodResultConceptTotalsByConceptName[DEFAULT_RESULT_CONCEPT.net_pay]?.total
          }
          totalPreviousPeriodInCents={
            otherPeriodResultConceptTotalsByConceptName[DEFAULT_RESULT_CONCEPT.net_pay]?.total
          }
          currency={currency}
        />

        <SummaryTotal
          title={i18n.t('payroll_bundle.results_payments.results.summary.total_cost')}
          totalCurrentPeriodInCents={
            currentPeriodResultConceptTotalsByConceptName[DEFAULT_RESULT_CONCEPT.company_cost]
              ?.total
          }
          totalPreviousPeriodInCents={
            otherPeriodResultConceptTotalsByConceptName[DEFAULT_RESULT_CONCEPT.company_cost]?.total
          }
          currency={currency}
        />

        <SummaryBox
          styles={{
            alignItems: 'flexEnd',
            justifyContent: 'center',
            width: 's140',
            minWidth: 's140',
          }}
        >
          <Button type="secondary" onClick={handleOpenSidepanel}>
            {i18n.t('payroll_bundle.results_payments.results.summary.see_more')}
          </Button>
        </SummaryBox>
      </Box>

      <ResultStatsSidepanel
        currentPeriodResultConceptTotals={currentPeriodResultConceptTotals}
        otherPeriodResultConceptTotalsByConceptName={otherPeriodResultConceptTotalsByConceptName}
        mappedResultConcepts={mappedResultConcepts}
        open={showResultStatsSidepanel}
        onClose={handleCloseSidepanel}
        currency={currency}
      />

      {showMissingEmployeesModal && (
        <MissingEmployeesModal
          title={i18n.t('payroll_bundle.payroll_results.missing_employees_modal.title')}
          description={
            policyPeriod && selectedPolicy
              ? i18n.t('payroll_bundle.payroll_results.missing_employees_modal.short_description', {
                  policyPeriodName: policyPeriod.attributes.name,
                })
              : ''
          }
          primaryButtonLabel={i18n.t(
            'payroll_bundle.payroll_results.missing_employees_modal.import_results_button_label'
          )}
          employees={filteredEmployees.filter(
            (employee) => !importedEmployeeIds.includes(employee.id)
          )}
          onClose={handleCloseMissingEmployeesModal}
          onPrimaryButtonClick={handleOpenImportResultsModal}
        />
      )}
    </>
  )
}

export default observer(ResultStats)
